import { skillAnimationFrameRange } from "@/character/skills/parse-skill-sheet";
import { SKILL_BUFF_FX_ALPHA, SKILL_DISPLAY_HEIGHT_PX } from "@/character/skills/constants";
import { addSkillSpritesheetTexture } from "@/game/load-data-texture";
import type { StatAllocation } from "@/game/model/character-stats";
import type { HomeSkillRuntimePayload, HomeSkillRuntimeSkill } from "@/home/home-events";
import { normalizeImageDataUrl } from "@/home/image-data-url";
import Phaser from "phaser";

const EMPTY_RUNTIME: HomeSkillRuntimePayload = {
  unlockedSlots: 0,
  cooldownMs: 30_000,
  slots: [],
  skills: [],
};

export class SceneSkillFx {
  private skillRuntime: HomeSkillRuntimePayload = EMPTY_RUNTIME;
  private skillFxSprite: Phaser.GameObjects.Sprite | null = null;
  private buffStopTimer: Phaser.Time.TimerEvent | null = null;
  private activeBuffBonus: Partial<StatAllocation> | null = null;
  private activeBuffUntil = 0;
  private readonly loadingSkillTextureKeys = new Set<string>();

  constructor(
    private readonly scene: Phaser.Scene,
    private readonly getPlayer: () => Phaser.GameObjects.Sprite,
  ) {}

  destroy(): void {
    this.clearSkillFx();
  }

  setRuntime(runtime: HomeSkillRuntimePayload): void {
    this.skillRuntime = runtime;
    this.registerAllSkillAnims();
  }

  buffStatBonus(): Partial<StatAllocation> | undefined {
    if (!this.activeBuffBonus || Date.now() > this.activeBuffUntil) return undefined;
    return this.activeBuffBonus;
  }

  hasActiveBuff(): boolean {
    return this.activeBuffBonus !== null && Date.now() <= this.activeBuffUntil;
  }

  getSkill(skillId: string): HomeSkillRuntimeSkill | undefined {
    return this.skillRuntime.skills.find((s) => s.id === skillId);
  }

  tickFollowPlayer(): void {
    if (!this.skillFxSprite?.active) return;
    const player = this.getPlayer();
    this.skillFxSprite.setPosition(player.x, player.y);
    this.skillFxSprite.setFlipX(player.flipX);
    this.skillFxSprite.setDepth(player.depth + 1);
  }

  playSkill(
    skillId: string,
    onAttackHit: (skill: HomeSkillRuntimeSkill, damageMultiplier: number) => void,
  ): void {
    const skill = this.skillRuntime.skills.find((s) => s.id === skillId);
    if (!skill) return;
    const player = this.getPlayer();

    if (skill.kind === "buff") {
      const duration = Math.max(500, skill.durationMs);
      this.buffStopTimer?.remove(false);
      this.playSkillFxAtPlayer(skill, true);
      this.activeBuffBonus = (skill.buffStatBonus as Partial<StatAllocation>) ?? null;
      this.activeBuffUntil = Date.now() + duration;
      this.buffStopTimer = this.scene.time.delayedCall(duration, () => {
        this.clearSkillFx();
      });
      if (this.scene.anims.exists("player-idle")) {
        player.play("player-idle", true);
      }
      return;
    }

    const mult = skill.damageMultiplier ?? 1.2;
    onAttackHit(skill, mult);

    if (this.scene.anims.exists("player-attack")) {
      player.play("player-attack");
    }
    this.playSkillFxAtPlayer(skill, false);
  }

  private skillTextureKey(skillId: string): string {
    return `home-skill-${skillId}`;
  }

  private skillAnimKey(skillId: string): string {
    return `skill-${skillId}`;
  }

  private registerAllSkillAnims(): void {
    for (const skill of this.skillRuntime.skills) {
      this.ensureSkillAnim(skill, false);
      this.ensureSkillAnim(skill, true);
    }
  }

  private skillTextureHasFrames(texKey: string, frameCount: number): boolean {
    if (!this.scene.textures.exists(texKey)) return false;
    return this.scene.textures.get(texKey).frameTotal >= frameCount;
  }

  private removeSkillTextureAndAnims(skillId: string): void {
    const texKey = this.skillTextureKey(skillId);
    const base = this.skillAnimKey(skillId);
    for (const animKey of [base, `${base}-loop`]) {
      if (this.scene.anims.exists(animKey)) this.scene.anims.remove(animKey);
    }
    if (this.scene.textures.exists(texKey)) this.scene.textures.remove(texKey);
  }

  private registerSkillAnim(skill: HomeSkillRuntimeSkill, loop: boolean): void {
    const texKey = this.skillTextureKey(skill.id);
    const animKey = loop ? `${this.skillAnimKey(skill.id)}-loop` : this.skillAnimKey(skill.id);
    if (this.scene.anims.exists(animKey)) return;
    if (!this.skillTextureHasFrames(texKey, skill.grid.columns)) return;
    const { start, end } = skillAnimationFrameRange(skill.grid);
    this.scene.anims.create({
      key: animKey,
      frames: this.scene.anims.generateFrameNumbers(texKey, { start, end }),
      frameRate: skill.frameRate,
      repeat: loop ? -1 : 0,
    });
  }

  private ensureSkillAnim(skill: HomeSkillRuntimeSkill, loop: boolean): string {
    const texKey = this.skillTextureKey(skill.id);
    const animKey = loop ? `${this.skillAnimKey(skill.id)}-loop` : this.skillAnimKey(skill.id);
    const frameCount = skill.grid.columns;

    if (this.skillTextureHasFrames(texKey, frameCount)) {
      this.registerSkillAnim(skill, loop);
      return animKey;
    }

    if (this.loadingSkillTextureKeys.has(texKey)) return animKey;
    this.loadingSkillTextureKeys.add(texKey);
    this.removeSkillTextureAndAnims(skill.id);

    const onAdd = (addedKey: string) => {
      if (addedKey !== texKey) return;
      this.scene.textures.off(Phaser.Textures.Events.ADD, onAdd);
      this.loadingSkillTextureKeys.delete(texKey);
      this.registerSkillAnim(skill, false);
      this.registerSkillAnim(skill, true);
    };
    this.scene.textures.on(Phaser.Textures.Events.ADD, onAdd);

    void addSkillSpritesheetTexture(
      this.scene.textures,
      texKey,
      normalizeImageDataUrl(skill.spritesheetDataUrl),
      skill.grid.frameWidth,
      skill.grid.frameHeight,
    ).catch(() => {
      this.scene.textures.off(Phaser.Textures.Events.ADD, onAdd);
      this.loadingSkillTextureKeys.delete(texKey);
    });

    return animKey;
  }

  private applySkillFxScale(sprite: Phaser.GameObjects.Sprite, skill: HomeSkillRuntimeSkill): void {
    let scale = SKILL_DISPLAY_HEIGHT_PX / skill.grid.frameHeight;
    if (skill.attackPattern === "aoe") {
      scale *= skill.aoeRangeMultiplier ?? 1;
    }
    sprite.setScale(scale);
  }

  private clearSkillFx(): void {
    this.buffStopTimer?.remove(false);
    this.buffStopTimer = null;
    if (this.skillFxSprite) {
      this.skillFxSprite.clearTint();
      this.skillFxSprite.destroy();
    }
    this.skillFxSprite = null;
    this.activeBuffBonus = null;
    this.activeBuffUntil = 0;
  }

  private playSkillFxAtPlayer(skill: HomeSkillRuntimeSkill, loop: boolean, onDone?: () => void): void {
    const texKey = this.skillTextureKey(skill.id);
    const animKey = this.ensureSkillAnim(skill, loop);
    const player = this.getPlayer();

    const startFx = () => {
      if (!this.scene.textures.exists(texKey)) return;
      this.clearSkillFx();
      this.skillFxSprite = this.scene.add.sprite(player.x, player.y, texKey, 0);
      this.skillFxSprite.setOrigin(0.5, 1);
      this.applySkillFxScale(this.skillFxSprite, skill);
      this.skillFxSprite.setDepth(player.depth + 1);
      this.skillFxSprite.setFlipX(player.flipX);
      this.skillFxSprite.setAlpha(loop ? SKILL_BUFF_FX_ALPHA : 1);
      if (!this.scene.anims.exists(animKey)) return;
      this.skillFxSprite.play(animKey);
      if (loop) return;
      this.skillFxSprite.once("animationcomplete", (animation: Phaser.Animations.Animation) => {
        if (animation.key !== animKey) return;
        this.clearSkillFx();
        onDone?.();
      });
    };

    if (this.scene.textures.exists(texKey) && this.scene.anims.exists(animKey)) {
      startFx();
      return;
    }
    const onAdd = (addedKey: string) => {
      if (addedKey !== texKey) return;
      this.scene.textures.off(Phaser.Textures.Events.ADD, onAdd);
      if (this.scene.anims.exists(animKey)) startFx();
    };
    this.scene.textures.on(Phaser.Textures.Events.ADD, onAdd);
    this.ensureSkillAnim(skill, loop);
  }
}
